import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { PRICE, PRICE_TRADE } from '../sbSharedRefineMenuConstants.ts';
import { getFilterValues, getAppliedFilter } from '../sbSharedRefineMenuHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';

/* Components */
import { SbSharedRefineMenuRadioSelect } from '../../SbSharedRefineMenu/SbSharedRefineMenuRadioSelect/SbSharedRefineMenuRadioSelect.jsx';

import { type SbSharedRefineMenuPriceRanges_itemSearch$key } from './__generated__/SbSharedRefineMenuPriceRanges_itemSearch.graphql.ts';
import { type SbSharedRefineMenuPriceRanges_user$key } from './__generated__/SbSharedRefineMenuPriceRanges_user.graphql.ts';

import dibsCss from 'dibs-css';

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuPriceRanges_itemSearch on ItemSearchQueryConnection {
        displayUriRef
        appliedFilters {
            name
            canBeDismissed
            values {
                urlLabel
                linkReference
            }
        }
        filters {
            name
            values {
                linkReference
                linkable
                displayName
                urlLabel
                currency
            }
        }
    }
`;
const userFragment = graphql`
    fragment SbSharedRefineMenuPriceRanges_user on User {
        isVerifiedTrade
    }
`;

type Props = {
    itemSearch: SbSharedRefineMenuPriceRanges_itemSearch$key;
    user: SbSharedRefineMenuPriceRanges_user$key;
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuPriceRanges: FC<Props> = ({
    itemSearch: itemSearchRef,
    user: userRef,
    trackingOptions,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const { isDesktop } = useServerVarsContext();
    const { currency } = useCurrency();
    const { displayUriRef = '', appliedFilters = [], filters = [] } = itemSearch;
    const isTradeUser = user?.isVerifiedTrade;
    const filterName = isTradeUser ? PRICE_TRADE : PRICE;
    const appliedFilterValues = getFilterValues(appliedFilters, filterName);
    const appliedFilter = getAppliedFilter(appliedFilters, filterName);
    const priceFilterValues = getFilterValues(filters, filterName);
    const valuesToShow = priceFilterValues.filter(filter => filter.currency === currency) || [];
    const canBeDismissed = appliedFilter ? appliedFilter.canBeDismissed : true;

    return (
        <div className={isDesktop ? dibsCss.mbXsmall : undefined}>
            <SbSharedRefineMenuRadioSelect
                filterName={filterName}
                valuesToShow={valuesToShow}
                displayUriRef={displayUriRef}
                canBeDismissed={canBeDismissed}
                appliedFilterValues={appliedFilterValues}
                currency={currency}
                trackInputIndex
                trackingOptions={trackingOptions}
            />
        </div>
    );
};
